/**
 * ValidationRequestModal
 *
 * Validation request for a job in "Engineering Completed" — a note plus an
 * optional invoice (reference + amount). Extracted from StatusBar for CWL-164 so
 * the invoice validation is unit-testable; the caller keeps the mutation, toasts
 * and cache invalidation, mirroring RequestMoreInfoOrReplyModal.
 *
 * CWL-164: an over-long or punctuated reference, or an unbounded amount, broke
 * CORE's Validation tab. Both fields stay optional — empty is valid — but an
 * invalid keystroke is blocked, and a value that is non-empty and invalid shows
 * an inline error and disables Submit.
 */
import {
  INVOICE_AMOUNT_VALIDATION_MSG,
  INVOICE_REFERENCE_MAX_LENGTH,
  INVOICE_REFERENCE_VALIDATION_MSG,
  isAllowedInvoiceAmountInput,
  isValidInvoiceAmount,
  isValidInvoiceReference,
} from '@/shared/lib/validation';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { cn } from '@/shared/ui/utils';
import { useEffect, useState } from 'react';

export interface ValidationRequestSubmission {
  note: string;
  invoiceReference?: string;
  invoiceAmount?: number;
}

interface ValidationRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  onSubmit: (submission: ValidationRequestSubmission) => void;
}

export function ValidationRequestModal({
  open,
  onOpenChange,
  isSubmitting,
  onSubmit,
}: ValidationRequestModalProps) {
  const [note, setNote] = useState('');
  const [invoiceReference, setInvoiceReference] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');

  // The caller closes the dialog on a successful submit by flipping `open`, which
  // never routes through onOpenChange — reset here so the next open starts clean.
  useEffect(() => {
    if (open) return;
    setNote('');
    setInvoiceReference('');
    setInvoiceAmount('');
  }, [open]);

  // Derived during render, not in an effect. Both fields are optional, so empty
  // is valid; anything typed must be valid.
  const showReferenceError = invoiceReference !== '' && !isValidInvoiceReference(invoiceReference);
  const showAmountError = invoiceAmount !== '' && !isValidInvoiceAmount(invoiceAmount);
  const isValid = !showReferenceError && !showAmountError;

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      note,
      invoiceReference: invoiceReference || undefined,
      invoiceAmount: invoiceAmount !== '' ? Number(invoiceAmount) : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Validation Request</DialogTitle>
          <DialogDescription>Submit a validation request for this job.</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="validation-note" className="text-foreground block">
              Note
            </Label>
            <Textarea
              id="validation-note"
              placeholder="Add a note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
          <div className="space-y-2">
            <span className="text-sm font-medium text-foreground block">Invoice</span>
            <div className="flex gap-2">
              <div className="flex-1 space-y-1">
                <Label
                  htmlFor="validation-invoice-reference"
                  className="text-xs font-normal text-muted-foreground"
                >
                  Reference
                </Label>
                <Input
                  id="validation-invoice-reference"
                  placeholder="INV-001"
                  value={invoiceReference}
                  maxLength={INVOICE_REFERENCE_MAX_LENGTH}
                  onChange={(e) => setInvoiceReference(e.target.value)}
                  className={cn(showReferenceError && 'border-red-500 focus-visible:ring-red-500')}
                  aria-invalid={showReferenceError}
                />
                {showReferenceError ? (
                  <p className="text-xs text-red-500">{INVOICE_REFERENCE_VALIDATION_MSG}</p>
                ) : null}
              </div>
              <div className="flex-1 space-y-1">
                <Label
                  htmlFor="validation-invoice-amount"
                  className="text-xs font-normal text-muted-foreground"
                >
                  Amount
                </Label>
                <Input
                  id="validation-invoice-amount"
                  placeholder="0.00"
                  value={invoiceAmount}
                  onChange={(e) => {
                    if (isAllowedInvoiceAmountInput(e.target.value)) {
                      setInvoiceAmount(e.target.value);
                    }
                  }}
                  inputMode="decimal"
                  className={cn(showAmountError && 'border-red-500 focus-visible:ring-red-500')}
                  aria-invalid={showAmountError}
                />
                {showAmountError ? (
                  <p className="text-xs text-red-500">{INVOICE_AMOUNT_VALIDATION_MSG}</p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="text-white"
            style={{ backgroundColor: '#E84A2B' }}
            disabled={isSubmitting || !isValid}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
